import {
  GEMINI_VERDICTS,
  type GeminiProposal,
  type GeminiSuggestion,
  type GeminiVerdict,
  MAX_RATIONALE_CHARS,
  SUGGESTION_KINDS,
} from '~/contracts/gemini/response'
import type { IsoTimestamp } from '~/types/dates'
import type { AiProposalId } from '~/types/ids'
import { newId } from '~/types/ids'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'
import { issue, type RecordEnvelope, type ValidationIssues } from './common'
import { DB_SCHEMA_VERSION } from './schemaVersion'

/**
 * A stored second opinion (D-094 §5).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  IMMUTABLE ONCE WRITTEN, AND THE SCHEMA ALREADY SAYS SO
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  `schema.ts` states the store's whole contract: *"Opaque id; requestedAt orders
 *  the history, which is the only lookup. A proposal is immutable once written,
 *  so there is nothing to find it by other than when it was asked for."*
 *
 *  **That is why there is no `weeklyReviewId` here and no second index.** Linking
 *  a proposal to the review it answered would be a lookup the schema says does not
 *  exist, and it would need a migration to add. If a future checkpoint genuinely
 *  needs one, it changes the schema and the rationale together — it does not
 *  quietly add a field and scan.
 *
 *  **There is no update path and there must not be one.** The repository offers
 *  `addAiProposal` and `listAiProposals`, and a test asserts it exports nothing
 *  else. An edited second opinion is a second opinion nobody gave.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  WHAT IT CARRIES, AND WHY EACH PIECE IS NOT OPTIONAL
 * ────────────────────────────────────────────────────────────────────────────
 *
 *  D-094 §4 requires `promptVersion`, the contract version and the model id on
 *  every stored proposal, so **a proposal can always say what produced it** —
 *  which is what makes "why was last month's advice different?" answerable rather
 *  than a shrug. `disclosureProfile` joins them from D-093: it records whether the
 *  user opted measurements into that particular request, and it is the only way to
 *  know later what was actually sent.
 *
 *  **The rationale stored here has already passed every validation gate.** A
 *  response that tripped one of D-094's five prose rules is discarded whole and
 *  never reaches this file — a tripped check names the rule and never shows or
 *  stores the raw text. Nothing below re-checks the prose, and nothing below
 *  should: a second guard for that rule would be the weaker of the two, and the
 *  Worker's is the one that is provably load-bearing.
 */
export interface AiProposalRecord extends RecordEnvelope {
  readonly id: AiProposalId
  /** When the user asked. Indexed, and the only ordering the history has. */
  readonly requestedAt: IsoTimestamp
  readonly verdict: GeminiVerdict
  readonly rationale: string
  readonly suggestions: readonly GeminiSuggestion[]
  /** What produced it (D-094 §4). All three are required, never inferred. */
  readonly promptVersion: string
  readonly contractVersion: number
  readonly model: string
  /** Whether measurements were included in the request that produced it (D-093). */
  readonly disclosureProfile: 'default' | 'withMeasurements'
}

export interface AiProposalInput {
  readonly proposal: GeminiProposal
  readonly disclosureProfile: 'default' | 'withMeasurements'
  readonly requestedAt: IsoTimestamp
}

/**
 * Whether this is worth keeping.
 *
 * **Deliberately structural rather than semantic.** It checks that the reply is
 * shaped like something the app can render and attribute later; it does not read
 * the prose, because the Worker already refused anything that broke a rule and
 * D-093 is explicit that structured output is never accepted as semantic
 * validation. Two guards for one rule, with only one of them load-bearing, is the
 * pattern this codebase deletes rather than adds.
 */
export function validateAiProposal(input: AiProposalInput): ValidationIssues {
  const issues = []
  const { proposal } = input

  if (!(GEMINI_VERDICTS as readonly string[]).includes(proposal.verdict)) {
    issues.push(issue('verdict', `A verdict must be one of ${GEMINI_VERDICTS.join(', ')}.`))
  }

  /* An empty rationale is the failure mode that matters: a verdict with no
     reasoning is a form rather than a review, which is exactly what D-094 §1
     rejected when it refused a fully structured response. */
  if (proposal.rationale.trim() === '') {
    issues.push(issue('rationale', 'A second opinion must say why, not only whether.'))
  }
  if (proposal.rationale.length > MAX_RATIONALE_CHARS) {
    issues.push(
      issue('rationale', `A rationale must be ${MAX_RATIONALE_CHARS} characters or fewer.`),
    )
  }

  for (const suggestion of proposal.suggestions) {
    if (!(SUGGESTION_KINDS as readonly string[]).includes(suggestion.kind)) {
      issues.push(issue('suggestions', 'A suggestion kind outside the allowlist cannot be stored.'))
    }
  }

  if (proposal.promptVersion.trim() === '') {
    issues.push(issue('promptVersion', 'A proposal must record the prompt that produced it.'))
  }
  if (proposal.model.trim() === '') {
    issues.push(issue('model', 'A proposal must record the model that produced it.'))
  }
  if (!Number.isInteger(proposal.contractVersion)) {
    issues.push(issue('contractVersion', 'A proposal must record its contract version.'))
  }

  return issues
}

export function buildAiProposalRecord(
  input: AiProposalInput,
  now: IsoTimestamp,
): Result<AiProposalRecord, ValidationIssues> {
  const issues = validateAiProposal(input)
  if (issues.length > 0) return err(issues)

  const { proposal } = input

  return ok({
    id: newId<'AiProposalId'>(),
    requestedAt: input.requestedAt,
    verdict: proposal.verdict,
    rationale: proposal.rationale,
    /* Copied field by field rather than spread, the same rule the wire payload
       builder follows: a field added to `GeminiProposal` later must be considered
       here deliberately rather than arriving in the record unnoticed. */
    suggestions: proposal.suggestions.map((suggestion) => ({ ...suggestion })),
    promptVersion: proposal.promptVersion,
    contractVersion: proposal.contractVersion,
    model: proposal.model,
    disclosureProfile: input.disclosureProfile,
    createdAt: now,
    /* Equal to `createdAt` for the life of the record. The envelope requires the
       field; immutability is what keeps the two the same, and a divergence would
       mean something edited a proposal. */
    updatedAt: now,
    schemaVersion: DB_SCHEMA_VERSION,
  })
}
